import { topologicalSort } from '@examdojo/core/widget';
import type { GeometryElement } from '../geometrypad.model';
import type {
  SolvedShape,
  SolvedTriangle,
  SolvedRectangle,
  SolvedSquare,
  SolvedCircle,
  SolvedParallelogram,
  SolvedRhombus,
  SolvedTrapezoid,
  SolvedArc,
} from '../analytics';
import type { PlacedShape } from '../placement';
import type { CreatedGeometryElement, GeometryPadElementContext } from './element.model';
import {
  createTriangle,
  createRectangle,
  createSquare,
  createCircle,
  createParallelogram,
  createRhombus,
  createTrapezoid,
  createArc,
  createPointElement,
  createLineElement,
  createPolygonElement,
} from './shapes';
import { type DiagonalElement, createDiagonal } from './features/diagonal';
import { type AltitudeElement, createAltitude } from './features/altitude';
import { type MedianElement, createMedian } from './features/median';
import { type MidsegmentElement, createMidsegment } from './features/midsegment';

/**
 * Create all JSXGraph elements from placed shapes.
 *
 * Iterates through elements in their original order (which should be
 * dependency-sorted already), creates JSXGraph objects via the appropriate
 * creator, and registers ports in the registry.
 */
export function createAllElements(
  elements: GeometryElement[],
  placedShapes: Map<string, PlacedShape>,
  solvedShapes: Map<string, SolvedShape>,
  context: GeometryPadElementContext,
): Map<string, CreatedGeometryElement> {
  const created = new Map<string, CreatedGeometryElement>();

  for (const element of elements) {
    // Skip annotation elements (handled separately after shapes)
    if (element.type === 'LengthAnnotation' || element.type === 'ArrowAnnotation') {
      continue;
    }

    let result: CreatedGeometryElement;

    // Features derive positions from parent ports — no PlacedShape needed
    if (element.type === 'Diagonal') {
      result = createDiagonal(element as DiagonalElement & { id: string }, context);
    } else if (element.type === 'Altitude') {
      result = createAltitude(element as AltitudeElement & { id: string }, context);
    } else if (element.type === 'Median') {
      result = createMedian(element as MedianElement & { id: string }, context);
    } else if (element.type === 'Midsegment') {
      result = createMidsegment(element as MidsegmentElement & { id: string }, context);
    } else {
      // Shapes require a PlacedShape for world-space vertices
      const placed = placedShapes.get(element.id);
      if (!placed) {
        continue;
      }

      const solved = solvedShapes.get(element.id) ?? null;

      if (element.type === 'Triangle') {
        result = createTriangle(element, placed, solved as SolvedTriangle, context);
      } else if (element.type === 'Rectangle') {
        result = createRectangle(element, placed, solved as SolvedRectangle, context);
      } else if (element.type === 'Square') {
        result = createSquare(element, placed, solved as SolvedSquare, context);
      } else if (element.type === 'Circle') {
        result = createCircle(element, placed, solved as SolvedCircle, context);
      } else if (element.type === 'Parallelogram') {
        result = createParallelogram(element, placed, solved as SolvedParallelogram, context);
      } else if (element.type === 'Rhombus') {
        result = createRhombus(element, placed, solved as SolvedRhombus, context);
      } else if (element.type === 'Trapezoid') {
        result = createTrapezoid(element, placed, solved as SolvedTrapezoid, context);
      } else if (element.type === 'Arc') {
        result = createArc(element, placed, solved as SolvedArc, context);
      } else if (element.type === 'Point') {
        const glideTarget = context.glideTargets.get(element.id);
        result = createPointElement(element, placed, context, glideTarget);
      } else if (element.type === 'Line') {
        result = createLineElement(element, placed, context);
      } else if (element.type === 'Polygon') {
        result = createPolygonElement(element, placed, context);
      } else {
        continue;
      }
    }

    // Register ports
    context.portRegistry.register(element.id, result.ports);
    created.set(element.id, result);
  }

  return created;
}

/**
 * Determine element processing order based on value dependencies.
 *
 * Elements that reference other elements via port expressions
 * must be created after the elements they reference.
 */
export function resolveElementOrder(elements: GeometryElement[]): GeometryElement[] {
  const elementMap = new Map<string, GeometryElement>();
  for (const el of elements) {
    elementMap.set(el.id, el);
  }

  const sortedIds = topologicalSort(elementMap, (_id, el) => {
    const refs = extractPortReferences(el);
    const deps: string[] = [];
    for (const ref of refs) {
      const depId = ref.split('.')[0];
      if (depId !== el.id) {
        deps.push(depId);
      }
    }
    return deps;
  });

  return sortedIds.map((id) => elementMap.get(id)!);
}

/**
 * Extract all port reference strings from an element's params.
 * Scans scalar Value expressions, PointTarget ref fields, and EdgeTarget ref fields.
 */
function extractPortReferences(element: GeometryElement): string[] {
  const refs: string[] = [];

  const extractFromValue = (val: unknown) => {
    if (typeof val === 'string') {
      // Extract port references from expressions
      // Match patterns like "elementId.portName" (identifiers with dots)
      const matches = (val as string).match(/[a-zA-Z_]\w*\.[a-zA-Z_][\w.[]]*/g);
      if (matches) {
        refs.push(...matches);
      }
    }
  };

  const extractFromTarget = (val: unknown) => {
    if (typeof val !== 'object' || val === null) {
      return;
    }
    const obj = val as Record<string, unknown>;

    // PointTarget: { x: Value, y: Value } or EdgeTarget: { ref: string }
    if ('ref' in obj && typeof obj['ref'] === 'string') {
      extractFromValue(obj['ref']);
    }
    if ('x' in obj) {
      extractFromValue(obj['x']);
    }
    if ('y' in obj) {
      extractFromValue(obj['y']);
    }
  };

  if ('params' in element && element.params) {
    for (const [key, val] of Object.entries(element.params as Record<string, unknown>)) {
      if (key === 'glideOn' && typeof val === 'string') {
        extractFromValue(val);
      } else if (key === 'from' || key === 'to' || key === 'target' || key === 'of') {
        extractFromValue(val);
      } else if (key === 'vertices' && Array.isArray(val)) {
        for (const v of val) {
          extractFromTarget(v);
        }
      } else if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
        // PointTarget or EdgeTarget object
        extractFromTarget(val);
      } else {
        extractFromValue(val);
      }
    }
  }

  return refs;
}
